import React from "react";
import { C } from "../ipd-billing/constants";
import SvgIcon from "../ipd-billing/SvgIcon";
import { RULE_TYPE_STYLES } from "./engine";

export { C };

export const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "9px 12px",
  border: `1.5px solid ${C.border}`, borderRadius: 8,
  fontSize: 13, color: C.text, background: "#fff",
  fontFamily: "inherit", outline: "none",
};

export const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700, color: C.textSub,
  letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 5,
};

export const cardStyle = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
  boxShadow: "0 2px 8px rgba(13,35,63,0.06)",
};

export function Field({ label, children, style }) {
  return (
    <div style={{ ...style }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

export function Btn({ children, onClick, variant = "primary", size = "md", disabled, style, type = "button" }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    border: "none", borderRadius: 8, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit", opacity: disabled ? 0.65 : 1, transition: "filter 0.15s",
    fontSize: size === "sm" ? 12 : 13,
    padding: size === "sm" ? "6px 12px" : "9px 16px",
  };
  const variants = {
    primary: { background: C.primary, color: "#fff" },
    outline: { background: "transparent", color: C.text, border: `1.5px solid ${C.border}` },
    ghost: { background: "transparent", color: C.textSub, border: "none" },
    danger: { background: "transparent", color: C.danger, border: "none" },
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick} style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

export function Flash({ message, tone = "success", onDismiss }) {
  if (!message) return null;
  const tones = {
    success: { bg: "#d1f2db", color: "#145a32", border: "#a3d9b1" },
    error: { bg: "#fdecea", color: "#842029", border: "#f5c2c7" },
    warning: { bg: "#fff8e1", color: "#856404", border: "#ffe69c" },
  };
  const t = tones[tone] || tones.success;
  return (
    <div style={{
      padding: "10px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
      background: t.bg, color: t.color, border: `1px solid ${t.border}`, 
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
    }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SvgIcon name="check" size={15} color={t.color} />
        {message}
      </span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} style={{ border: "none", background: "none", cursor: "pointer", padding: 2 }}>
          <SvgIcon name="x" size={14} color={t.color} />
        </button>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, back, onBack, actions }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
      <div>
        {back && (
          <button type="button" onClick={onBack} style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: C.textSub, fontSize: 12, marginBottom: 6, padding: 0 }}>
            <SvgIcon name="back" size={14} color={C.textSub} />
            {back}
          </button>
        )}
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>{title}</h2>
        {subtitle && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
    </div>
  );
}

export function KpiTile({ icon, label, value, hint, tone = "primary" }) {
  const tones = {
    primary: { bg: "#E9F5FE", color: C.primary },
    success: { bg: "#d1f2db", color: C.success },
    info: { bg: "#e0e7ff", color: "#4338ca" },
    teal: { bg: "#d1f2db", color: "#0f766e" },
  };
  const t = tones[tone] || tones.primary;
  return (
    <div style={{ ...cardStyle, padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SvgIcon name={icon} size={18} color={t.color} />
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{value}</div>
        {hint && <div style={{ fontSize: 10, color: C.textSub }}>{hint}</div>}
      </div>
    </div>
  );
}

export function RuleTypeChip({ type }) {
  const s = RULE_TYPE_STYLES[type] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {type}
    </span>
  );
}

// `width` overrides the wide/narrow presets — used by editor-style modals
// (e.g. the radiology report editor) that need more room than 720px.
export function Modal({ open, onClose, title, description, children, footer, wide, width }) {
  if (!open) return null;
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: C.card, borderRadius: 14, width: width || (wide ? 720 : 520), maxWidth: "94vw",
        maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 16px 56px rgba(0,0,0,0.18)",
      }}>
        <div style={{ padding: "20px 24px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.text }}>{title}</h3>
              {description && <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>{description}</p>}
            </div>
            <button type="button" onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}>
              <SvgIcon name="x" size={18} color={C.textSub} />
            </button>
          </div>
        </div>
        <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: "14px 24px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ChipToggle({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${active ? C.primary : C.border}`,
        background: active ? C.primary : "#fff", color: active ? "#fff" : C.text,
        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

export function TabBar({ tabs, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 16 }}>
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
              fontSize: 13, fontWeight: active ? 700 : 500,
              color: active ? C.primary : C.textSub,
              borderBottom: active ? `2px solid ${C.primary}` : "2px solid transparent",
              marginBottom: -1, display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {t.icon && <SvgIcon name={t.icon} size={14} color={active ? C.primary : C.textSub} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function LoadingBlock({ message = "Loading…" }) {
  return (
    <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: 13 }}>
      {message}
    </div>
  );
}

export function DataTable({ columns, rows, emptyText, onRowAction }) {
  return (
    <div style={{ ...cardStyle, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f8f9fa", fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {columns.map((col) => (
              <th key={col.key} style={{ textAlign: col.align || "left", padding: "12px 16px" }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} style={{ borderTop: `1px solid ${C.border}` }} className="rb-table-row">
              {columns.map((col) => (
                <td key={col.key} style={{ padding: "12px 16px", textAlign: col.align || "left", verticalAlign: "middle" }}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: 13 }}>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <style>{`.rb-table-row:hover { background: #f8f9fa; }`}</style>
    </div>
  );
}
